import { useState, useEffect, useRef } from 'react';
import { djAPI, aiAPI, tenantsAPI } from '../services/api';
import { Mic, Play, Pause, Trash2, Plus, X, Zap, Save, Music, RefreshCw, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import TabBar from '../components/TabBar';

const AUDIO_BASE = 'https://software.radiostream.co.za/media/audio';

function AudioPlayer({ itemId, small }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(`${AUDIO_BASE}/${itemId}.mp3`);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      setLoading(true);
      audioRef.current.play()
        .then(() => { setPlaying(true); setLoading(false); })
        .catch(() => { setLoading(false); toast.error('Could not play audio'); });
    }
  };

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const size = small ? 11 : 13;
  return (
    <button className="btn btn-ghost btn-sm" onClick={toggle} style={{ color: playing ? 'var(--green) ': undefined, gap: 4 }}>
      {loading ? <div className="spinner" style={{ width: size, height: size }} /> : playing ? <Pause size={size} /> : <Play size={size} />}
      {!small && (playing ? 'Pause' : 'Play')}
    </button>
  );
}

function FeedbackModal({ item, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const reasons = ['Wrong tone', 'Too salesy', 'Wrong store name', 'Too long', 'Incorrect info', 'Poor quality', 'Other'];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">Remove Clip &amp; Give Feedback</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 0 16px' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
            Tell the AI why you're removing this clip so it learns not to repeat the same mistake.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {reasons.map(r => (
              <button key={r} className={`btn btn-sm ${reason === r ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setReason(r)}>{r}</button>
            ))}
          </div>
          <textarea className="form-textarea" style={{ minHeight: 80 }}
            placeholder="Add more detail (optional)..."
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            Script: <em>"{item.script_text?.slice(0, 80)}..."</em>
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onSubmit(reason)}>
            <Trash2 size={13} /> Remove &amp; Train AI
          </button>
        </div>
      </div>
    </div>
  );
}

function GenerateModal({ tenantId, voices, languages, onClose, onGenerated }) {
  const [form, setForm] = useState({ voice_id: '', language_id: '', clip_type: 'dj_clip', artist: '', song: '' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGenerate = async () => {
    if (!form.voice_id || !form.language_id) return toast.error('Select voice and language');
    setLoading(true);
    try {
      const params = { voice_id: form.voice_id, language_id: form.language_id, clip_type: form.clip_type };
      if (form.artist) params.artist = form.artist;
      if (form.song) params.song = form.song;
      await djAPI.generate(tenantId, params);
      toast.success('🎙️ AI DJ clip generated!');
      onGenerated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Generate AI DJ Clip</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 16 }}>
          <div className="form-group">
            <label className="form-label">Content Type</label>
            <select className="form-select" value={form.clip_type} onChange={e => set('clip_type', e.target.value)}>
              <option value="dj_clip">DJ Clip</option>
              <option value="ai_liner">Station Liner</option>
              <option value="ai_advert">In-Store Announcement</option>
            </select>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Language</label>
              <select className="form-select" value={form.language_id} onChange={e => { set('language_id', e.target.value); set('voice_id', ''); }}>
                <option value="">Select language...</option>
                {languages.map(l => <option key={l.id} value={l.id}>{ls.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Voice</label>
              <select className="form-select" value={form.voice_id} onChange={e => set('voice_id', e.target.value)} disabled={!form.language_id}>
                <option value="">Select voice...</option>
                {voices.filter(v => !form.language_id || v.language_id === form.language_id).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16 }}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              <Music size={13} style={{ marginRight: 4 }} />
              Optional: What song just played? The DJ will reference it.
            </p>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Artist</label>
                <input className="form-input" value={form.artist} onChange={e => set('artist', e.target.value)} placeholder="e.g. Tyla" />
              </div>
              <div className="form-group">
                <label className="form-label">Song</label>
                <input className="form-input" value={form.song} onChange={e => set('song', e.target.value)} placeholder="e.g. Water" />
              </div>
            </div>
          </div>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>AI will automatically use:</strong> current time of day, day of week, upcoming holidays, your store name and suggestion topics.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
            {loading ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Generating...</> : <><Zap size={14} /> Generate Now</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DJStudio() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tab, setTab] = useState('clips');
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ store_name: '', dj_name: 'Alex', tone: 'friendly and warm' });
  const [suggestions, setSuggestions] = useState([]);
  const [newSuggestion, setNewSuggestion] = useState('');
  const [clips, setClips] = useState([]);
  const [voices, setVoices] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [feedbackItem, setFeedbackItem] = useState(null);
  const [generateModal, setGenerateModal] = useState(false);

  useEffect(() => {
    Promise.all([tenantsAPI.list(), aiAPI.listVoices(), aiAPI.listLanguages()])
      .then(([t, v, l]) => { setTenants(t.data); setVoices(v.data); setLanguages(l.data); });
  }, []);

  useEffect(() => {
    if (!selectedTenant) return;
    loadAll();
  }, [selectedTenant]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [clipsRes, feedbackRes] = await Promise.all([
        aiAPI.listContent(selectedTenant),
        djAPI.getFeedback(selectedTenant).catch(() => ({ data: [] })),
      ]);
      setClips(clipsRes.data.filter(c => c.status !== 'rejected').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setFeedback(feedbackRes.data);

      try {
        const profileRes = await djAPI.getProfile(selectedTenant);
        setProfile(profileRes.data);
        setProfileForm({ store_name: profileRes.data.store_name, dj_name: profileRes.data.dj_name, tone: profileRes.data.tone });
        setSuggestions(profileRes.data.suggestion_topics || []);
      } catch {
        setProfile(null);
      }
    } finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await djAPI.saveProfile(selectedTenant, profileForm);
      toast.success('DJ profile saved');
      loadAll();
    } catch { toast.error('Failed to save'); }
    finally { setSavingProfile(false); }
  };

  const addSuggestion = async () => {
    if (!newSuggestion.trim()) return;
    try {
      await djAPI.addSuggestion(selectedTenant, newSuggestion.trim());
      setSuggestions(prev => [...prev, newSuggestion.trim()]);
      setNewSuggestion('');
      toast.success('Suggestion added');
    } catch { toast.error('Failed'); }
  };

  const removeSuggestion = async (s) => {
    try {
      await djAPI.removeSuggestion(selectedTenant, s);
      setSuggestions(prev => prev.filter(x => x !== s));
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  const handleFeedback = async (reason) => {
    try {
      await djAPI.submitFeedback(feedbackItem.id, reason);
      toast.success('Clip removed. AI will learn from this feedback.');
      setFeedbackItem(null);
      loadAll();
    } catch { toast.error('Failed'); }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' });
  };

  const tabs = [
    { id: 'clips', label: 'Clips', count: clips.length },
    { id: 'profile', label: 'DJ Profile' },
    { id: 'suggestions', label: 'Suggestions', count: suggestions.length },
    { id: 'feedback', label: 'AI Learning', count: feedback.length },
  ];

  return (
    <div>
      {feedbackItem && <FeedbackModal item={feedbackItem} onClose={() => setFeedbackItem(null)} onSubmit={handleFeedback} />}
      {generateModal && <GenerateModal tenantId={selectedTenant} voices={voices} languages={languages} onClose={() => setGenerateModal(false)} onGenerated={loadAll} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">DJ Studio</h1>
          <p className="page-subtitle">Autonomous AI DJ — generates contextual clips using Claude + ElevenLabs</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select className="form-select" style={{ width: 200 }} value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}>
            <option value="">Select brand...</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {selectedTenant && (
            <button className="btn btn-primary" onClick={() => setGenerateModal(true)}>
              <Zap size={15} /> Generate Now
            </button>
          )}
        </div>
      </div>

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {!selectedTenant ? (
        <div className="card">
          <div className="empty-state"><Mic size={48} /><h3>Select a brand</h3><p>Choose a brand to manage its AI DJ</p></div>
        </div>
      ) : (
        <>
          {loading ? <div className="card"><div className="loading-state"><div className="spinner" /></div></div> : (
            <>
              {tab === 'clips' && (
                <div className="card">
                  {clips.length === 0 ? (
                    <div className="empty-state">
                      <Mic size={48} />
                      <h3>No clips yet</h3>
                      <p>Click Generate Now to create your first autonomous DJ clip</p>
                      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setGenerateModal(true)}>
                        <Zap size={15} /> Generate First Clip
                      </button>
                    </div>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Name</th><th>Script</th><th>Duration</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
                        <tbody>
                          {clips.map(clip => (
                            <tr key={clip.id}>
                              <td>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{clip.name}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{clip.generation_params?.mode === 'autonomous' ? '🤖 Auto-generated' : '✍️ Manual'}</div>
                              </td>
                              <td><div style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clip.script_text || clip.input_text || '—'}</div></td>
                              <td style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{clip.duration_seconds ? `${clip.duration_seconds.toFixed(1)}s` : '—'}</td>
                              <td><span className={`badge badge-${clip.status === 'active' ? 'green' : clip.status === 'pending_review' ? 'purple' : 'grey'}`}>{clip.status}</span></td>
                              <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatTime(clip.created_at)}</td>
                              <td><div style={{ display: 'flex', gap: 4 }}><AudioPlayer itemId={clip.id} small /><button className="btn btn-danger btn-sm" onClick={() => setFeedbackItem(clip)} title="Remove &amp; give feedback"><Trash2 size={11} /></button></div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {tab === 'profile' && (
                <div className="card" style={{ maxWidth: 600 }}>
                  <h3 style={{ marginBottom: 20, fontSize: 16, fontWeight: 600 }}>DJ Personality Settings</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div className="form-group"><label className="form-label">Store Name</label><input className="form-input" value={profileForm.store_name} onChange={e => setProfileForm(f => ({ ...f, store_name: e.target.value }))} placeholder="e.g. Spar" /></div>
                    <div className="form-group"><label className="form-label">DJ Name</label><input className="form-input" value={profileForm.dj_name} onChange={e => setProfileForm(f => ({ ...f, dj_name: e.target.value }))} placeholder="e.g. Alex" /></div>
                    <div className="form-group"><label className="form-label">Tone &amp; Style</label><select className="form-select" value={profileForm.tone} onChange={e => setProfileForm(f => ({ ...f, tone: e.target.value }))}><option value="friendly and warm">Friendly and warm</option><option value="energetic and upbeat">Energetic and upbeat</option><option value="professional and authoritative">Professional and authoritative</option><option value="calm and relaxed">Calm and relaxed</option><option value="fun and playful">Fun and playful</option></select></div>
                    <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} onClick={saveProfile} disabled={savingProfile}>
                      {savingProfile ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving...</> : <><Save size={14} /> Save Profile</>}
                    </button>
                  </div>
                </div>
              )}

              {tab === 'suggestions' && (
                <div className="card" style={{ maxWidth: 600 }}>
                  <h3 style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}>Suggestion Topics</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Topics the AI DJ can naturally mention. It picks one that fits — it never forces them all in.</p>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    <input className="form-input" style={{ flex: 1 }} value={newSuggestion} onChange={e => setNewSuggestion(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSuggestion()} placeholder="e.g. bakery specials, loyalty card..." />
                    <button className="btn btn-primary" onClick={addSuggestion} disabled={!newSuggestion.trim()}><Plus size={14} /> Add</button>
                  </div>
                  {suggestions.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: 32 }}>No suggestions yet.</div> : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {suggestions.map(s => (
                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '6px 12px', fontSize: 13 }}>
                          {s}
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0 }} onClick={() => removeSuggestion(s)}><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tab === 'feedback' && (
                <div className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div><h3 style={{ fontSize: 16, fontWeight: 600 }}>AI Learning History</h3><p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Clips you've removed — the AI uses this feedback to avoid similar content in future.</p></div>
                    <button className="btn btn-ghost btn-sm" onClick={loadAll}><RefreshCw size={13} /> Refresh</button>
                  </div>
                  {feedback.length === 0 ? (
                    <div className="empty-state"><MessageSquare size={48} /><h3>No feedback yet</h3><p>When you remove clips and give a reason, the AI learns from it here.</p></div>
                  ) : (
                    <div className="table-wrap"><table><thead><tr><th>Reason</th><th>Script</th><th>Date</th></tr></thead><tbody>{feedback.map(fb => (<tr key={fb.id}><td><span className="badge badge-red">{fb.reason || 'No reason given'}</span></td><td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fb.script_text ? `"${fb.script_text.slice(0, 100)}..."` : '—'}</td><td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatTime(fb.created_at)}</td></tr>))}</tbody></table></div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default DJStudio;
